/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useAccessTokens } from "../api/hooks.js";
import { session, currentAccount } from "../stores/session.js";
import { useTranslation } from "../context/translation.js";
import {
  CreateAccessScreen,
  loginTokenScope,
} from "../screens/CreateAccessScreen.js";
import type { PendingProtectedAction } from "../api/protectedAction.js";
import { runProtectedMutation } from "../api/contracts.js";

export interface CreateAccessRouteProps {
  issuedToken: string | null;
  onIssuedTokenChange: (token: string | null) => void;
  onMfaRequired: (pending: PendingProtectedAction) => void;
}

export function CreateAccessRoute({
  issuedToken,
  onIssuedTokenChange,
  onMfaRequired,
}: CreateAccessRouteProps): VNode {
  const { t } = useTranslation();
  const { createToken } = useAccessTokens();
  const hasToken = Boolean(session.value.token);

  return (
    <CreateAccessScreen
      generatedToken={issuedToken}
      onGeneratedTokenChange={onIssuedTokenChange}
      onSave={async (data) => {
        if (hasToken && createToken) {
          const scope = loginTokenScope(data.canDo, data.isRefreshable);

          let d_us: number | "forever" = 30 * 86400 * 1000000;
          if (data.expires === "90d") d_us = 90 * 86400 * 1000000;
          else if (data.expires === "365d") d_us = 365 * 86400 * 1000000;
          else if (data.expires === "never") d_us = "forever";

          const tokenPayload = {
            description: data.usedFor,
            scope,
            duration: { d_us },
            password: data.password,
          };

          const res = await createToken(tokenPayload);

          if (res && res.mfaRequired && res.challengeResponse) {
            onMfaRequired({
              accountLabel: currentAccount.value || t`Merchant Account`,
              actionNotice: t`Creating machine access token (${data.usedFor})`,
              challengeResponse: res.challengeResponse,
              cancelTo: "/access",
              continueWith: (challengeIds) =>
                runProtectedMutation(async () => {
                  const continued = await createToken(
                    tokenPayload,
                    challengeIds,
                  );
                  if (continued?.mfaRequired && continued.challengeResponse) {
                    return {
                      type: "challenge" as const,
                      challenge: continued.challengeResponse,
                    };
                  }
                  if (!continued?.token) {
                    throw new Error(
                      t`The backend did not return a machine access token.`,
                    );
                  }
                  onIssuedTokenChange(continued.token);
                  return { redirectTo: "/access/new" };
                }),
            });
            return { mfaRequired: true };
          }

          if (!res?.token) {
            throw new Error(
              t`The backend did not return a machine access token.`,
            );
          }
          return res.token;
        }
        throw new Error(t`Machine access creation is unavailable.`);
      }}
    />
  );
}
